import { LOCALE_CODES, type LocaleCode } from "@/lib/locales/types";

export const SITE_URL = "https://carentals.ro";

type SharedPageKey = "country" | "airport-otopeni" | "cheap";

const TRANSLATED_SLUGS: Record<LocaleCode, Record<SharedPageKey, string>> = {
  de: { country: "", "airport-otopeni": "mietwagen-otopeni-flughafen", cheap: "guenstiger-mietwagen-rumaenien" },
  it: { country: "", "airport-otopeni": "noleggio-auto-bucarest-aeroporto-otopeni", cheap: "noleggio-auto-economico-romania" },
  he: { country: "", "airport-otopeni": "haskarat-rehev-shdeh-teufa-otopeni", cheap: "haskarat-rehev-zola" },
  es: { country: "", "airport-otopeni": "alquiler-coche-aeropuerto-bucarest-otopeni", cheap: "alquiler-de-coches-baratos-rumania" },
  fr: { country: "", "airport-otopeni": "location-voiture-aeroport-bucarest-otopeni", cheap: "location-voiture-pas-cher-roumanie" },
  pl: { country: "", "airport-otopeni": "wynajem-samochodu-bukareszt-lotnisko-otopeni", cheap: "tani-wynajem-samochodu-rumunia" },
  nl: { country: "", "airport-otopeni": "autohuur-otopeni", cheap: "goedkope-autohuur-roemenie" },
};

const EN_PAGE_KEYS: Record<string, SharedPageKey> = {
  "/": "country",
  "/car-rental-otopeni-airport": "airport-otopeni",
  "/car-rental-no-deposit-bucharest": "cheap",
};

const translatedPath = (code: LocaleCode, key: SharedPageKey) => {
  const slug = TRANSLATED_SLUGS[code][key];
  return slug ? `/${code}/${slug}` : `/${code}`;
};

export type Lang = "en" | "ro";

/** Pairs of equivalent EN / RO pages used for the language switch and hreflang tags. */
export const LANG_PAIRS: Array<{ en: string; ro: string }> = [
  { en: "/", ro: "/ro" },
  { en: "/car-rental-no-deposit-bucharest", ro: "/ro/inchirieri-auto-fara-garantie" },
  { en: "/car-rental-otopeni-airport", ro: "/ro/inchirieri-auto-otopeni" },
];

export const isRomanianPath = (pathname: string) =>
  pathname === "/ro" || pathname.startsWith("/ro/");

export function counterpartPath(pathname: string): string {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const pair = LANG_PAIRS.find((p) => p.en === clean || p.ro === clean);
  if (pair) return isRomanianPath(clean) ? pair.en : pair.ro;
  // No translated equivalent: RO pages fall back to the RO home, EN pages to the EN home.
  return isRomanianPath(clean) ? "/" : "/ro";
}

/** hreflang <link> entries for a translated page pair. */
export function hreflangLinks(enPath: string, roPath: string) {
  // Additive: when the page has translated mirrors under /de /it /he /es /fr /pl /nl,
  // advertise them too. The EN/RO URLs themselves never change.
  const key = EN_PAGE_KEYS[enPath];
  const translated = key
    ? LOCALE_CODES.map((code) => ({
        rel: "alternate",
        hrefLang: code,
        href: `${SITE_URL}${translatedPath(code, key)}`,
      }))
    : [];
  return [
    { rel: "alternate", hrefLang: "en", href: `${SITE_URL}${enPath}` },
    { rel: "alternate", hrefLang: "ro", href: `${SITE_URL}${roPath}` },
    ...translated,
    { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${enPath}` },
  ];
}

/** Google Business Profile of Car Rental Bucharest (used for local SEO alignment). */
export const GOOGLE_PLACE_ID = "ChIJrdkPfoH_sUARTaPpd56qkrU";
export const GOOGLE_MAPS_PROFILE = `https://www.google.com/maps/place/?q=place_id:${GOOGLE_PLACE_ID}`;
export const GOOGLE_REVIEW_URL = `https://search.google.com/local/writereview?placeid=${GOOGLE_PLACE_ID}`;
